import React from "react";
import OrderTable from "./OrderTable";
import DiningSection from "./DiningSection";
import PaginationComponent from "../../../utils/TablePagination";
import LoadingScreen from "../../../utils/LoadingScreen";

const CurrentOrdersTable = ({
  filter,
  filteredOrders,
  dispatchedOrderIds = [],
  newOrderIds = [],
  handleRowClickCurrent,
  openRowCurrent,
  orderDetails,
  selectedDeliveryPerson,
  getTimeElapsed,
  handlePrintClick,
  handleDirectClick,
  setActionModalOrder,
  setCancelModalOrder,
  reciptDetails,
  KitchenPrint,
  getDiningStatusLabel,
  handleCancel,
  setCashModalOrder,
  updateOrderStatus,
  getNewOrders,
  getOrderHistory,
  startDate,
  endDate,
  getOrderDetails,
  loadingOrders,
  setTakeawayOrder,
  forceSchedulePink,
}) => {
  const renderOrderStatus = (order) => {
    const status = order.status?.toLowerCase()?.trim();
    const type = order.orderType;
    const deliveryStatus = order.delivery_partner_status?.toLowerCase()?.trim();
    const paymentStatus = order.payment_status?.toLowerCase()?.trim();
    const isPartialFlag = order.is_partial?.toUpperCase()?.trim();
    const partialPaidFlag = order.partial_paid?.toUpperCase()?.trim();
    const isPartial = isPartialFlag === "Y";
    const partialAmount = Number(order.partial_amount || 0);

    const formattedPartialAmount = Number.isFinite(partialAmount)
      ? partialAmount.toFixed(2)
      : "0.00";

    const mode = order.payment_mode
      ? order.payment_mode.charAt(0).toUpperCase() + order.payment_mode.slice(1)
      : "";

    // Keep final / operational statuses above payment labels.
    if (status === "rejected" || status === "refunded") {
      return (
        <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-600 font-semibold">
          {status.charAt(0).toUpperCase() + status.slice(1)}
        </span>
      );
    }

    if (status === "prepared") {
      return (
        <span className="inline-flex items-center justify-center w-[170px] h-8 text-sm rounded-full bg-green-100 text-green-600 font-semibold">
          Prepared
        </span>
      );
    }

    if (type === "home_delivery" && deliveryStatus === "food is ready") {
      return (
        <span className="inline-flex items-center justify-center w-[170px] h-8 rounded-full bg-yellow-200 text-yellow-700 text-sm font-semibold whitespace-nowrap">
          Dispatched
        </span>
      );
    }

    // Partial payment selected but not paid yet.
    if (isPartial && !(partialPaidFlag === "Y" && paymentStatus === "paid")) {
      return (
        <span className="inline-flex items-center justify-center min-w-[170px] h-8 px-3 text-xs rounded-full bg-amber-100 text-amber-700 font-semibold whitespace-nowrap">
          Partial Pending ₹{formattedPartialAmount}
        </span>
      );
    }

    // Partial payment successfully paid.
    if (isPartial && partialPaidFlag === "Y" && paymentStatus === "paid") {
      const partialPercentage = String(order.parial_percentage || "").trim();

      return (
        <span className="inline-flex items-center gap-2 min-w-[190px] h-9 px-2.5 rounded-full bg-white border border-green-200 text-xs font-semibold whitespace-nowrap">
          <span className="text-green-700">Partial Paid</span>
          <span className="inline-flex items-center justify-center px-2.5 py-1 rounded-full bg-green-600 text-white font-bold">
            {partialPercentage ? `${partialPercentage}% · ` : ""}₹{formattedPartialAmount}
          </span>
        </span>
      );
    }

    // No partial payment selected + payment completed = full payment.
    if (
      isPartialFlag === "N" &&
      partialPaidFlag === "N" &&
      paymentStatus === "paid"
    ) {
      return (
        <span className="inline-flex items-center justify-center min-w-[150px] h-8 px-3 text-xs rounded-full bg-green-100 text-green-600 font-semibold whitespace-nowrap">
          {mode ? `${mode} ` : ""}Full Paid
        </span>
      );
    }

    // Existing received behaviour for unpaid/non-partial orders.
    if (status === "received") {
      return (
        <span className="text-sm px-3 py-2 rounded-full bg-blue-100 text-blue-600 font-semibold">
          {type === "dining"
            ? `Table ${order.table_no} Active`
            : "Waiting for Payment"}
        </span>
      );
    }

    // Existing paid fallback, in case an older API response only updates status.
    if (status === "paid") {
      return (
        <span className="inline-flex items-center justify-center w-[190px] h-8 text-sm rounded-full bg-green-100 text-green-600 font-semibold">
          {mode} Paid & Preparing
        </span>
      );
    }

    return (
      <span className="inline-flex items-center justify-center w-[170px] h-8 text-sm rounded-full bg-red-100 text-red-600 font-semibold">
        Not Paid
      </span>
    );
  };

  const formatScheduleDateTime = (dateTime) => {
    if (!dateTime) return "";

    const value = String(dateTime).trim();
    if (!value) return "";

    const [datePart, timePart] = value.split(" ");
    if (!datePart || !timePart) return value;

    const [year, month, day] = datePart.split("-");
    const [hour, minute] = timePart.split(":");

    let h = Number(hour);
    const ampm = h >= 12 ? "PM" : "AM";
    h = h % 12 || 12;

    return `${day}/${month}/${year} ${h}:${minute} ${ampm}`;
  };

  const renderOrderType = (order) => {
    const type = (order.orderType || order.order_type || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_");

    const config = {
      home_delivery: {
        label: "Home Delivery",
        className: "bg-green-100 text-green-700 border border-green-200",
      },
      takeaway: {
        label: "Takeaway",
        className: "bg-yellow-100 text-yellow-700 border border-yellow-200",
      },
      dining: {
        label: "Dining",
        className: "bg-purple-100 text-purple-700 border border-purple-200",
      },
      car_order: {
        label: "Car Order",
        className: "bg-blue-100 text-blue-700 border border-blue-200",
      },

      office_order: {
        label: "Office Order",
        className: "bg-orange-100 text-orange-700 border border-orange-200",
      },
    };

    const item = config[type] || {
      label: type ? type.replace(/_/g, " ") : "N/A",
      className: "bg-gray-100 text-gray-700 border border-gray-200",
    };

    return (
      <span
        className={`inline-flex items-center justify-center w-[120px] py-2 text-sm font-semibold rounded-lg capitalize ${item.className}`}
      >
        {item.label}
      </span>
    );
  };

  const getScheduleDateTime = (order) => {
    return (
      order?.scheduled_date ||
      order?.schedule_date ||
      order?.scheduleDate ||
      order?.scheduledDate ||
      order?.schedule_time ||
      order?.scheduled_time ||
      ""
    );
  };

  const parseScheduleDateTime = (scheduledDate) => {
    if (!scheduledDate) return null;

    // API format: 2026-06-06 10:00:00
    const value = String(scheduledDate).trim();
    if (!value) return null;

    const [datePart, timePart] = value.split(" ");
    if (!datePart || !timePart) return null;

    const [year, month, day] = datePart.split("-").map(Number);
    const [hours, minutes, seconds] = timePart.split(":").map(Number);

    if (
      !year ||
      !month ||
      !day ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      return null;
    }

    return new Date(year, month - 1, day, hours, minutes, seconds || 0);
  };

  const parseDeliveryTimeToMilliseconds = (deliveryTime) => {
    const value = String(deliveryTime ?? "").trim();

    if (!value) return 0;

    if (value.includes(".")) {
      const [minutePart = "0", secondPart = "0"] = value.split(".");

      const minutes = Number(minutePart) || 0;

      let seconds = Number(secondPart) || 0;
      if (secondPart.length === 1) {
        seconds *= 10;
      }

      return minutes * 60 * 1000 + seconds * 1000;
    }

    const minutes = Number(value);

    if (!Number.isFinite(minutes)) {
      return 0;
    }

    return minutes * 60 * 1000;
  };

  const formatDispatchActiveTime = (timestamp) => {
    if (!timestamp) return "";

    const date = new Date(timestamp);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    return date.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const isScheduleOrder = (order) => {
    const orderType = (order?.orderType || order?.order_type || "")
      .toLowerCase()
      .trim();

    const status = order?.status?.toLowerCase()?.trim();

    return (
      orderType === "schedule" ||
      orderType === "scheduled" ||
      status === "schedule" ||
      status === "scheduled" ||
      Boolean(String(getScheduleDateTime(order) || "").trim())
    );
  };

const getScheduleDispatchActiveTime = (order) => {
  const scheduledDate = getScheduleDateTime(order);

  if (!scheduledDate) {
    return null;
  }

  const scheduleTime = parseScheduleDateTime(scheduledDate);

  if (!scheduleTime) {
    return null;
  }

  const deliveryTimeMs = parseDeliveryTimeToMilliseconds(order?.delivery_time);

  const additionalBufferMs = 10 * 60 * 1000;

  const totalBufferMs = deliveryTimeMs + additionalBufferMs;

  return scheduleTime.getTime() - totalBufferMs;
};

const isScheduleDispatchAllowed = (order) => {
 
  if (!getScheduleDateTime(order)) {
    return true;
  }

  const activeFrom = getScheduleDispatchActiveTime(order);

  if (!activeFrom) {
    return false;
  }

  return Date.now() >= activeFrom;
};

  const isOrderAlreadyDispatched = (order) => {
    const status = order.status?.toLowerCase()?.trim();
    const deliveryStatus = order.delivery_partner_status?.toLowerCase()?.trim();

    return (
      status === "dispatched" ||
      status === "ready_for_takeaway" ||
      status === "ready_for_dinning" ||
      status === "delivered" ||
      status === "completed" ||
      deliveryStatus === "food is ready" ||
      deliveryStatus === "dispatched"
    );
  };

  const renderActionButton = (order) => {
    const status = order.status?.toLowerCase()?.trim();

    const type = (order.orderType || order.order_type || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_");

    const paymentStatus = order.payment_status?.toLowerCase()?.trim();

    const isPaid =
      paymentStatus === "paid" ||
      status === "paid" ||
      order.payment_mode?.toLowerCase()?.trim() === "cod";

    const deliveryStatus = order.delivery_partner_status?.toLowerCase()?.trim();

    const isDispatching = dispatchedOrderIds.includes(order.orderid);

    const scheduleOrder = isScheduleOrder(order);
    const scheduleAllowed = isScheduleDispatchAllowed(order);

    const dispatchActiveTimestamp = getScheduleDispatchActiveTime(order);

    const dispatchActiveTime = formatDispatchActiveTime(
      dispatchActiveTimestamp,
    );

if (scheduleOrder && !scheduleAllowed) {
  return (
    <div className="flex justify-start">
      <div className="w-[150px] rounded-md border border-pink-300 bg-pink-50 px-2.5 py-1.5 text-center">
        <p className="text-[12px] font-medium leading-4 text-pink-600">
          Dispatch this order at{" "}
          <span className="font-bold">
            {dispatchActiveTime || "Calculating..."}
          </span>
        </p>
      </div>
    </div>
  );
}

    const alreadyDispatched = isOrderAlreadyDispatched(order);

    const cancelBtn = (
      <button
        onClick={(e) => {
          e.stopPropagation();
          setCancelModalOrder(order);
        }}
        className="inline-flex items-center justify-center w-[100px] h-10 text-sm bg-red-500 text-white rounded"
      >
        Cancel
      </button>
    );

    if (alreadyDispatched) {
      return (
        <span className="inline-flex items-center justify-center w-[160px] h-10 rounded text-sm bg-gray-100 text-gray-500 font-semibold">
          Already Dispatched
        </span>
      );
    }

    // =========================
    // HOME DELIVERY
    // =========================
    if (type === "home_delivery") {
      const disabled = !isPaid || isDispatching;

      return (
        <div className="flex flex-col gap-1">
          <div className="flex gap-2">
            <button
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();

                if (disabled) return;

                setActionModalOrder(order);
              }}
              className={`relative inline-flex items-center justify-center w-[160px] h-10 rounded ${
                disabled
                  ? "border border-gray-400 text-gray-400 bg-white cursor-not-allowed"
                  : deliveryStatus === "acknowledged"
                    ? "bg-green-600 text-white"
                    : deliveryStatus === "food is ready"
                      ? "bg-yellow-500 text-black"
                      : "border border-green-600 text-green-600 bg-white"
              }`}
            >
              {isDispatching ? "Dispatching..." : "Dispatch Order"}

              {deliveryStatus === "acknowledged" && !disabled && (
                <span className="absolute -top-4 left-8 bg-yellow-300 text-yellow-800 text-[12px] px-1.5 py-0.5 rounded-full font-bold">
                  Assigned
                </span>
              )}
            </button>

            {cancelBtn}
          </div>

          {scheduleOrder && !scheduleAllowed && (
            <span className="text-[10px] text-pink-600 font-semibold">
              Dispatch will be active 10 minutes before schedule time
            </span>
          )}
        </div>
      );
    }

    // =========================
    // UNPAID / RECEIVED
    // =========================
    if (status === "received" && !isPaid) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setCashModalOrder(order);
          }}
          className="inline-flex items-center justify-center w-[160px] h-10 bg-green-500 text-white rounded text-sm"
        >
          Collect Cash
        </button>
      );
    }

    // =========================
    // TAKEAWAY / CAR / OFFICE
    // =========================
    if (
      ["paid", "accepted", "prepared", "received"].includes(status) &&
      ["takeaway", "car_order", "office_order"].includes(type)
    ) {
     const disabled = isDispatching;

      return (
        <div className="flex flex-col gap-1">
          <div className="flex gap-2">
            <button
              disabled={disabled}
              onClick={(e) => {
                e.stopPropagation();

                if (disabled) return;

                if (type === "takeaway") {
                  setTakeawayOrder(order);
                  return;
                }

                handleDirectClick(order);
              }}
              className={`px-3 py-2 rounded text-sm font-medium disabled:opacity-60 disabled:cursor-not-allowed ${
                type === "office_order"
                  ? "border border-orange-500 text-orange-600 bg-orange-50 hover:bg-orange-100"
                  : "border border-blue-500 text-blue-600 bg-white hover:bg-blue-50"
              }`}
            >
              {isDispatching ? "Dispatching..." : "Dispatch Order"}
            </button>

            {isPaid && cancelBtn}
          </div>
        </div>
      );
    }

    // =========================
    // DINING
    // =========================
    if (
      ["paid", "accepted", "prepared"].includes(status) &&
      type === "dining"
    ) {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            updateOrderStatus({
              userid: order.userid,
              orderid: order.orderid,
              productid: "",
              status: "ready_for_dinning",
              time: "",
              payment: "",
            });
          }}
          className="px-3 py-2 bg-blue-500 text-white rounded text-sm"
        >
          Prepare
        </button>
      );
    }

    if (status === "rejected" || status === "refunded") {
      return (
        <span className="text-xs text-red-500">{order.remark || "NA"}</span>
      );
    }

    return (
      <button
        disabled
        className="px-3 py-1 bg-gray-300 text-gray-500 text-xs rounded"
      >
        No Action
      </button>
    );
  };

  const renderPaymentStatus = (order) => {
    const s = order.payment_status?.toLowerCase()?.trim();

    //  Unpaid
    if (s !== "paid") {
      return (
        <span className="text-xs px-2 py-1 rounded text-white bg-gray-700">
          00:00
        </span>
      );
    }

    //  Paid → time calculate
    const timeData = getTimeElapsed(order.order_date, order.payment_status);

    return (
      <span
        className={`text-xs px-2 py-1 rounded text-white ${
          timeData.isLate ? "bg-red-600" : "bg-green-600"
        }`}
      >
        {timeData.time}
      </span>
    );
  };

  const formatOrderType = (order) => {
    return (order.orderType || order.order_type || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "_");
  };

  const getOrderPreparationMinutes = () => {
    try {
      const userData = JSON.parse(sessionStorage.getItem("user") || "{}");
      const minutes = Number(userData?.order_time);

      return Number.isFinite(minutes) && minutes > 0 ? minutes : 0;
    } catch {
      return 0;
    }
  };

  const parseOrderDateTime = (orderDate) => {
    if (!orderDate) return null;

    const [datePart, timePart, period] = String(orderDate).split(" ");

    if (!datePart || !timePart) return null;

    const [day, month, year] = datePart.split("/").map(Number);
    let [hours, minutes, seconds] = timePart.split(":").map(Number);

    if (
      !day ||
      !month ||
      !year ||
      Number.isNaN(hours) ||
      Number.isNaN(minutes)
    ) {
      return null;
    }

    if (period?.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (period?.toUpperCase() === "AM" && hours === 12) hours = 0;

    return new Date(year, month - 1, day, hours, minutes, seconds || 0);
  };

  const [timerTick, setTimerTick] = React.useState(Date.now());

  React.useEffect(() => {
    const interval = setInterval(() => {
      setTimerTick(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const isFoodReadyButtonActiveForRow = (order) => {
    const type = formatOrderType(order);
    const paymentStatus = order.payment_status?.toLowerCase()?.trim();
    const deliveryStatus = order.delivery_partner_status?.toLowerCase()?.trim();

    // only home delivery paid orders
    if (type !== "home_delivery") return false;
    if (paymentStatus !== "paid") return false;

    if (deliveryStatus === "food is ready") return false;

    const preparationMinutes = getOrderPreparationMinutes();
    const orderDateTime = parseOrderDateTime(order.order_date);

    if (!preparationMinutes || !orderDateTime) return true;

    const allowedTime =
      orderDateTime.getTime() + preparationMinutes * 60 * 1000;

    return timerTick >= allowedTime;
  };

  const [visibleNewIds, setVisibleNewIds] = React.useState([]);

  React.useEffect(() => {
    if (newOrderIds.length > 0) {
      newOrderIds.forEach((id) => {
        setVisibleNewIds((prev) => (prev.includes(id) ? prev : [...prev, id]));

        setTimeout(() => {
          setVisibleNewIds((prev) => prev.filter((item) => item !== id));
        }, 120000);
      });
    }
  }, [newOrderIds]);

  const [currentPage, setCurrentPage] = React.useState(1);
  const itemsPerPage = 15;

  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const paginatedOrders = filteredOrders.slice(startIndex, endIndex);

  if (filter === "rejected") {
    return (
      <>
        <table className="w-full text-sm montserrat-font-1 ">
          <thead className="bg-green-100 montserrat-font-1 ">
            <tr>
              <th className="p-4 text-start">S.No</th>
              <th className="p-4 text-start">Order ID</th>
              <th className="p-4 text-start">Date</th>
              <th className="p-4 text-start">Time</th>
              <th className="p-4 text-start">Customer</th>
              <th className="p-4 text-start">Type</th>
              <th className="p-4 text-start">Status</th>
              <th className="p-4 text-start">Reason</th>
            </tr>
          </thead>

          <tbody>
            {loadingOrders ? (
              <tr>
                <td colSpan="8" className="text-center py-10">
                  <LoadingScreen text="Loading orders..." />
                </td>
              </tr>
            ) : paginatedOrders.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-6 text-gray-500">
                  No Data Found
                </td>
              </tr>
            ) : (
              paginatedOrders.map((order, index) => {
                const isNew = visibleNewIds.includes(order.orderid);

                const scheduleRow =
                  forceSchedulePink ||
                  (isScheduleOrder && isScheduleOrder(order));

                return (
                  <React.Fragment key={order.orderid}>
                    <tr
                      className={`border-t cursor-pointer transition ${
                        scheduleRow
                          ? "bg-pink-100 hover:bg-pink-200 border-l-4 border-pink-500"
                          : isNew
                            ? "animate-slideInRight bg-green-50 hover:bg-green-50 border-l-4 border-green-500"
                            : "hover:bg-green-50"
                      }`}
                      onClick={() =>
                        handleRowClickCurrent(index, order.orderid)
                      }
                    >
                      <td className="p-4">{index + 1}</td>
                      <td>
                        {order.orderid}
                        {isNew && (
                          <span className="ml-2 text-[10px] bg-green-500 text-white px-1.5 py-0.5 rounded-full font-bold">
                            NEW
                          </span>
                        )}
                      </td>
                      <td>{order.order_date?.split(" ")[0]}</td>
                      <td>{renderPaymentStatus(order)}</td>
                      <td>
                        <div>{order.customer_name || "N/A"}</div>
                        <div className="text-xs text-gray-500">
                          {order.customer_mobile || "N/A"}
                        </div>
                      </td>

                      <td>{renderOrderType(order)}</td>
                      <td>{order.status}</td>
                      <td>{order.remark || "—"}</td>
                    </tr>

                    {openRowCurrent === index && (
                      <tr className="bg-gray-100">
                        <td colSpan="8">
                          <OrderTable
                            orderDetails={orderDetails}
                            selectedDeliveryPerson={selectedDeliveryPerson}
                            activeTab="current"
                            reciptDetails={reciptDetails}
                          />
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
      </>
    );
  }

  if (filter === "dining") {
    if (loadingOrders) {
      return <LoadingScreen text="Loading dining orders..." />;
    }

    if (filteredOrders.length === 0) {
      return (
        <div className="text-center py-10 text-gray-500">No Data Found</div>
      );
    }

    return (
      <DiningSection
        orders={filteredOrders}
        onRowClick={handleRowClickCurrent}
        onCancel={handleCancel}
        onCollectCash={setCashModalOrder}
        updateOrderStatus={updateOrderStatus}
        getNewOrders={getNewOrders}
        getOrderHistory={getOrderHistory}
        startDate={startDate}
        endDate={endDate}
        getTimeElapsed={getTimeElapsed}
        getDiningStatusLabel={getDiningStatusLabel}
        getOrderDetails={getOrderDetails}
      />
    );
  }

  return (
    <>
      <div className="relative w-full h-full montserrat-font-1">
        <table className="w-full table-fixed text-sm montserrat-font-1">
          <thead className="sticky top-0 z-10 bg-green-100">
            <tr>
              <th className="w-[90px] p-3 text-start">Daily OR No</th>
              <th className="w-[200px] p-3 text-start">Order ID</th>
              <th className="w-[120px] p-3 text-start">Date</th>
              <th className="w-[80px] p-3 text-start">Time</th>
              <th className="w-[150px] p-3 text-start">Customer</th>
              <th className="w-[130px] p-3 text-start">Type</th>
              <th className="w-[150px] p-3 text-start">Status</th>
              <th className="w-[180px] p-3 text-start">Action</th>
              <th className="w-[80px] p-3 text-start">Print</th>
            </tr>
          </thead>

          <tbody>
            {loadingOrders ? (
              <tr>
                <td colSpan="9" className="text-center py-10">
                  <LoadingScreen text="Loading orders..." />
                </td>
              </tr>
            ) : paginatedOrders.length === 0 ? (
              <tr>
                <td colSpan="9" className="text-center py-6 text-gray-500">
                  No Data Found
                </td>
              </tr>
            ) : (
              paginatedOrders.map((order, index) => {
                const isNew = visibleNewIds.includes(order.orderid);

                const scheduleRow = forceSchedulePink || isScheduleOrder(order);

                const scheduleAllowed = isScheduleDispatchAllowed(order);

                const isFoodReadyActive =
                  isFoodReadyButtonActiveForRow(order) &&
                  (!scheduleRow || scheduleAllowed);

                return (
                  <React.Fragment key={order.orderid}>
                    <tr
                      className={`border-t cursor-pointer montserrat-font-1 transition ${
                        scheduleRow
                          ? "bg-pink-100 hover:bg-pink-200 border-l-4 border-pink-500"
                          : isFoodReadyActive
                            ? "bg-blue-100 hover:bg-blue-200 border-l-4 border-blue-500"
                            : isNew
                              ? "animate-slideInRight bg-green-50 hover:bg-green-50 border-l-4 border-green-500"
                              : "hover:bg-green-50"
                      }`}
                      onClick={() =>
                        handleRowClickCurrent(index, order.orderid)
                      }
                    >
                      <td className="p-8 align-middle">
                        <strong>{order.daily_order_id || "N/A"}</strong>
                      </td>

                      <td className="text-start align-middle px-3 py-2 overflow-hidden">
                        <div className="flex items-start gap-3 w-[230px] max-w-[230px]">
                          {/* LEFT SIDE: Order info */}
                          <div className="flex flex-col items-start gap-1 min-w-[95px] max-w-[115px]">
                            <span className="text-[11px] font-bold text-black whitespace-nowrap">
                              ORD: {order.orderid}
                            </span>

                            {getScheduleDateTime(order) && (
                              <span className="text-[12px] text-pink-700 font-semibold whitespace-nowrap leading-none">
                                {formatScheduleDateTime(
                                  getScheduleDateTime(order),
                                )}
                              </span>
                            )}

                            {order.srNo && (
                              <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap">
                                SR: {order.srNo}
                              </span>
                            )}
                          </div>

                          {/* RIGHT SIDE: Badges */}
                          <div className="flex flex-col items-start gap-1 min-w-[85px]">
                            {isFoodReadyActive && (
                              <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-blue-600 text-white text-[9px] font-bold leading-none whitespace-nowrap">
                                FOOD READY ACTIVE
                              </span>
                            )}

                            {scheduleRow && (
                              <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-pink-500 text-white text-[9px] font-bold leading-none whitespace-nowrap">
                                SCHEDULE
                              </span>
                            )}

                            {isNew && (
                              <span className="inline-flex items-center justify-center px-2 py-1 rounded-full bg-green-500 text-white text-[9px] font-bold leading-none whitespace-nowrap">
                                NEW
                              </span>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="text-sm">
                        <div>{order.order_date?.split(" ")[0]}</div>
                        <div className="text-xs text-gray-500">
                          {order.order_date?.split(" ").slice(1).join(" ")}{" "}
                        </div>
                      </td>

                      <td>{renderPaymentStatus(order)}</td>

                      <td>
                        <div>{order.customer_name}</div>
                        <div className="text-sm text-gray-900">
                          {order.customer_mobile}
                        </div>
                      </td>

                      <td>{renderOrderType(order)}</td>

                      <td>{renderOrderStatus(order)}</td>

                      <td>{renderActionButton(order)}</td>

                      <td className="space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrintClick(order);
                          }}
                          className="inline-flex items-center justify-center w-[100px] h-10 bg-blue-500 rounded text-white"
                        >
                          Print
                        </button>

                        {/* <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDirectClick(order);
                      }}
                      className="px-3 py-2 bg-green-500 rounded text-white"
                    >
                      Direct
                    </button> */}
                      </td>
                    </tr>

                    {openRowCurrent === index && (
                      <tr className="bg-gray-50">
                        <td colSpan="9" className="p-0">
                          <div className="w-full max-w-full overflow-hidden border-t border-gray-200">
                            <div className="w-full max-w-full overflow-x-auto p-3 bg-blue-50">
                              <div className="min-w-full">
                                <OrderTable
                                  orderDetails={orderDetails}
                                  selectedDeliveryPerson={
                                    selectedDeliveryPerson
                                  }
                                  activeTab="current"
                                  handleKitchenPrint={KitchenPrint}
                                  reciptDetails={reciptDetails}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            )}
          </tbody>
        </table>
        <PaginationComponent
          currentPage={currentPage}
          totalPages={totalPages}
          onNextPage={() =>
            setCurrentPage((prev) => Math.min(prev + 1, totalPages))
          }
          onPrevPage={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
          onPageClick={(page) => setCurrentPage(page)}
        />
      </div>
    </>
  );
};

export default CurrentOrdersTable;